import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import {
  approvePost,
  savePost,
  fetchPosts,
} from "../../features/admin/PostsSlice";
import { toast } from "react-toastify";
import { convertToSlug } from "../../utils/helps";
import ApiCustom from "../../utils/ApiCustom";

const postSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .min(5, "Tiêu đề phải ít nhất 5 ký tự")
    .required("Tiêu đề không được để trống"),

  content: Yup.string()
    .trim()
    .test("content-length", "Nội dung không được để trống", (value) => {
      return value && value.trim().length >= 10;
    })
    .required("Nội dung không được để trống"),

  images: Yup.mixed(),

  userEmails: Yup.array(),
});

const PostPreview = ({ postId, onClose }) => {
  const [post, setPost] = useState(null);
  const [formData, setFormData] = useState({
    title: post?.title || "",
    content: post?.content || null,
    status: post?.status || "draft",
    seoTitle: post?.seoTitle || "",
    seoDescription: post?.seoDescription || "",
    seoKeywords: post?.seoKeywords || "",
    cateGory: post?.cateGory || "",
    images: null,
    categoryId: post?.categoryId || "",
    userEmails: post?.userEmails || [],
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch();

  const { status: saveStatus } = useSelector((state) => state.posts);

  useEffect(() => {
    if (postId) {
      getPosts();
    }
  }, []);

  useEffect(() => {
    if (post) {
      setFormData({
        title: post?.title || "",
        content: post?.content || null,
        status: post?.status || "draft",
        seoTitle: post?.seoTitle || "",
        seoDescription: post?.seoDescription || "",
        seoKeywords: post?.seoKeywords || "",
        cateGory: post?.cateGory || "",
        images: null,
        categoryId: post?.categoryId || "",
      });
    }
  }, [post]);

  const getPosts = () => {
    ApiCustom.get(`/posts/${postId}`)
      .then((res) => {
        setPost(res.data?.data);
      })
      .catch((err) => console.log(err));
  };

  const handleChange = async (e) => {
    const name = e.name || e.target?.name;
    const value = e.value || e.target?.value;
    const files = e.target?.files;
    let newValue = name === "images" && files ? files[0] : value;
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    try {
      await Yup.reach(postSchema, name).validate(newValue);
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    } catch (validationError) {
      setErrors((prev) => ({
        ...prev,
        [name]: validationError.message,
      }));
    }
  };

  const handleSubmit = async (e, actionType) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const finalFormData = {
        ...formData,
        status: actionType === "save" ? "published" : "feedback",
      };
      let validationSchema = postSchema;

      if (post?.id && !formData.images) {
        validationSchema = postSchema.omit(["images"]);
      }
      await validationSchema.validate(finalFormData, { abortEarly: false });
      const submitData = new FormData();
      let data = {
        status: finalFormData.status,
        notifyEmails: formData?.userEmails || [],
        cateGory: formData?.cateGory,
      };

      Object.keys(finalFormData).forEach((key) => {
        const value = finalFormData[key];
        if (value !== null && value !== undefined) {
          if (key === "userEmails" && Array.isArray(value)) {
            // Xử lý mảng email người dùng
            value.forEach((email, index) => {
              submitData.append(`notifyEmails[${index}]`, email);
            });
          } else {
            submitData.append(key, value);
          }
        }
      });

      submitData.append("slug", convertToSlug(finalFormData?.title));

      try {
        await (post?.id
          ? dispatch(approvePost({ id: post.id, newPost: data }))
          : dispatch(savePost(submitData))
        ).unwrap();
        toast.success("Thành công!");
        dispatch(fetchPosts());
        setIsSubmitting(false);
      } catch (error) {
        console.error("Error submitting post:", error);
        toast.error("Có lỗi xảy ra!");
        setIsSubmitting(false);
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError && error.inner) {
        const formErrors = {};
        error.inner.forEach((err) => {
          formErrors[err.path] = err.message;
        });
        setErrors(formErrors);
      }
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-base-100 p-6 rounded-lg w-3/4 max-h-[90vh] overflow-y-auto">
        <form>
          <div className="w-full">
            <div className="flex justify-between items-center ">
              <h1 className="text-3xl font-bold mb-4">{formData?.title}</h1>
              <button
                onClick={onClose}
                className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
              >
                X
              </button>
            </div>
            <div className="flow-root">
              <div className="sun-editor-editable">
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: formData?.content }}
                />
              </div>
            </div>
            <div className="mb-4">
              <div className="mb-4">
                <label htmlFor="title" className="block text-sm font-medium">
                  Ghi chú
                </label>
                <textarea
                  name="cateGory"
                  placeholder="Ghi chú"
                  value={formData.cateGory}
                  onChange={handleChange}
                  className="textarea textarea-bordered w-full"
                  rows="2"
                ></textarea>

                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 btn btn-sm rounded btn-active btn-ghost"
                disabled={isSubmitting}
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={(e) => handleSubmit(e, "reject")}
                className="px-4 py-2 btn rounded btn-sm"
                disabled={isSubmitting || saveStatus === "loading"}
              >
                {isSubmitting || saveStatus === "loading" ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Đang lưu...
                  </span>
                ) : (
                  "Phản hồi"
                )}
              </button>
              <button
                type="button"
                onClick={(e) => handleSubmit(e, "save")}
                className="px-4 py-2 btn btn-primary rounded btn-sm"
                disabled={isSubmitting || saveStatus === "loading"}
              >
                {isSubmitting || saveStatus === "loading" ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Đang lưu...
                  </span>
                ) : (
                  "Duyệt"
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostPreview;
